import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import FileUpload from "../../components/FileUpload/FileUpload";
import useApiCall from "../../hooks/useApiCall";
import { INDIGO, SLATE } from "../../constants/Colors";
import { ACCEPTED_LOGO_FORMATS } from "../../constants/Constants";
import { usePageMeta } from "../../contexts/PageMetaContext";
import BottomActionBar from "../../components/BottomActionBar";
import TrimTextField from "../../components/TrimTextField";

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

function SectionHeader({ title, description }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: SLATE[900] }}>
        {title}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], mt: "2px" }}>
          {description}
        </Typography>
      )}
    </Box>
  );
}

const EMPTY_FORM = {
  name: "",
  unit_code: "",
  url: "",
  logo: "",
  metadata: { description: "" },
};

export default function ManageBusinessUnit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const originalDataRef = useRef(EMPTY_FORM);
  const isDirty = JSON.stringify(formData) !== JSON.stringify(originalDataRef.current);

  useEffect(() => {
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEdit || !currentApp?.id) return;
    setLoading(true);
    makeRequest(`/v1/apps/${currentApp?.id}/business_units/${id}`, { method: "GET", authTokens: tokens })
      .then((data) => {
        const unit = data?.business_unit;
        if (unit) {
          const loaded = {
            name: unit.name || "",
            unit_code: unit.unit_code || "",
            url: unit.url || "",
            logo: unit.logo || "",
            metadata: { description: unit.metadata?.description || "" },
          };
          originalDataRef.current = loaded;
          setFormData(loaded);
          setPageMeta({ itemName: unit.name });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, currentApp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleMetaChange = (field, value) => {
    setFormData((prev) => ({ ...prev, metadata: { ...prev.metadata, [field]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      name: formData.name,
      url: formData.url,
      logo: formData.logo,
      metadata: formData.metadata,
      app_id: currentApp.id,
      ...(!isEdit && { unit_code: formData.unit_code }),
    };

    try {
      if (isEdit) {
        await makeRequest(`/v1/apps/${currentApp.id}/business_units/${id}`, {
          method: "PUT",
          body: { business_unit: payload },
          authTokens: tokens,
        });
      } else {
        await makeRequest(`/v1/apps/${currentApp.id}/business_units`, {
          method: "POST",
          body: { business_unit: payload },
          authTokens: tokens,
        });
      }
      navigate(`/${currentApp.url_code}/business-units`);
    } catch (_err) {
      // error already toasted by useApiCall
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 10 }}>
        <CircularProgress size={28} sx={{ color: INDIGO[500] }} />
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={(e) => { e.preventDefault(); handleSave(); }}
      sx={{ width: "100%", maxWidth: 720, pb: 10 }}
    >
      {/* Basic Information */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "16px",
          p: 3,
          mb: 2.5,
        }}
      >
        <SectionHeader
          title="Basic Information"
          description="The core details used to identify this business unit."
        />

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
          {/* Name */}
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Typography component="label" sx={FIELD_LABEL_SX}>Business Unit Name <Box component="span" sx={{ color: "#ef4444" }}>*</Box></Typography>
            <TrimTextField
              fullWidth
              size="small"
              required
              placeholder="e.g. ASU Online"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              sx={INPUT_SX}
              inputProps={{
                onInvalid: (e) => e.target.setCustomValidity("Please enter a business unit name"),
                onInput: (e) => e.target.setCustomValidity(""),
              }}
            />
          </Box>

          {/* Unit Code */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Unit Code <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
              <Typography component="span" sx={{ fontSize: "0.75rem", color: SLATE[400], ml: 1 }}>
                (alphanumeric + underscores)
              </Typography>
            </Typography>
            <TrimTextField
              fullWidth
              size="small"
              required
              disabled={isEdit}
              placeholder="e.g. asu_online"
              value={formData.unit_code}
              onChange={(e) => handleChange("unit_code", e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
              sx={INPUT_SX}
              inputProps={{
                onInvalid: (e) => e.target.setCustomValidity("Please enter a unit code"),
                onInput: (e) => e.target.setCustomValidity(""),
              }}
            />
          </Box>

          {/* Website URL */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Website URL</Typography>
            <TrimTextField
              fullWidth
              size="small"
              placeholder="https://example.com"
              value={formData.url}
              onChange={(e) => handleChange("url", e.target.value)}
              sx={INPUT_SX}
            />
          </Box>

          {/* Description */}
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Typography component="label" sx={FIELD_LABEL_SX}>Description</Typography>
            <TrimTextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              placeholder="Describe this business unit's purpose and context..."
              value={formData.metadata.description}
              onChange={(e) => handleMetaChange("description", e.target.value)}
              sx={INPUT_SX}
            />
          </Box>
        </Box>
      </Box>

      {/* Logo */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "16px",
          p: 3,
          mb: 2.5,
        }}
      >
        <SectionHeader
          title="Logo"
          description="Upload a logo to display on the business units list."
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "12px",
              border: `1px solid ${SLATE[200]}`,
              bgcolor: SLATE[50],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {formData.logo ? (
              <img src={formData.logo} alt="logo" style={{ width: 48, height: 48, objectFit: "contain" }} />
            ) : (
              <Typography sx={{ fontSize: "0.6875rem", color: SLATE[400] }}>No logo</Typography>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <TrimTextField
              fullWidth
              size="small"
              placeholder="https://example.com/logo.png"
              value={formData.logo}
              onChange={(e) => handleChange("logo", e.target.value)}
              sx={{ ...INPUT_SX, mb: 1 }}
            />
            <FileUpload
              field={{ name: "fileUpload", title: formData.logo ? "Change Logo" : "Upload Logo", multiple: false }}
              accept={ACCEPTED_LOGO_FORMATS}
              endpoint={`/v1/appearances/upload_file?appId=${currentApp?.id}`}
              onChange={(e) => handleChange("logo", e.target.value[0])}
            />
          </Box>
        </Box>
      </Box>

      <BottomActionBar
        name={isEdit ? "Save Business Unit" : "Create Business Unit"}
        handleCancel={() => navigate(`/${currentApp?.url_code}/business-units`)}
        saving={saving}
        isDirty={isDirty}
      />
    </Box>
  );
}
